import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { AlertCircle, Send } from 'lucide-react';

const sydneyLocalDatetime = () => {
  // Return YYYY-MM-DDTHH:MM for datetime-local input, in Sydney tz.
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Sydney',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date()).reduce((a, p) => (a[p.type] = p.value, a), {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

export default function LodgeConfirmDialog({ open, onClose, onConfirm, pack }) {
  const [lodgedAt, setLodgedAt] = useState(sydneyLocalDatetime());
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    // Convert local time to ISO 8601 string
    const iso = new Date(lodgedAt).toISOString();
    try {
      await onConfirm(iso);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="lodge-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Send className="w-4 h-4 text-blue-600" />
            Confirm lodgement with {pack?.lender}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            This locks the pack from further submission edits. The SLA countdown will begin from the lodgement time.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border-2 px-3 py-2 flex items-start gap-2"
          style={{ background: '#eff6ff', borderColor: '#93c5fd' }}>
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: '#1d4ed8' }} />
          <div className="text-[13px] font-semibold" style={{ color: '#1e3a8a' }}>
            Has this application now been officially lodged with the lender?
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-700 mb-1 block">Lodgement date &amp; time (Sydney)</label>
          <Input type="datetime-local" value={lodgedAt}
            onChange={e => setLodgedAt(e.target.value)}
            data-testid="lodge-datetime" className="text-sm" />
          <p className="text-[11px] text-slate-500 mt-1">
            Defaults to the current time — change it if the file was lodged earlier.
          </p>
        </div>

        <div className="rounded-md px-3 py-2 text-[11px]" style={{ background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0' }}>
          <b>What happens next:</b>
          <ul className="mt-1 space-y-0.5 list-disc list-inside">
            <li>Status set to <b>Lodged with Lender</b></li>
            <li>Pack moves from Active to the <b>Post-Lodgement</b> dashboard</li>
            <li>Expected response calculated from your <b>Initial Assessment</b> SLA (business days, weekends + AU holidays excluded)</li>
            <li>MIR checklist unlocks — add requests as the lender issues them</li>
          </ul>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={submitting}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="lodge-confirm-btn">
            {submitting ? 'Lodging…' : 'Yes — lodge with lender'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
